import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import classNames from "classnames";
import { useParams } from "react-router-dom";
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalBody,
  Button,
} from "reactstrap";

import { useTranslation } from "react-i18next";
import { useDeleteDeal } from "../../api/deals/service/index";
import { useGetAccount } from "api/account/service";
import { getActiveColorStyle } from "../../utils/colorUtils";
import {
  formatAccountStatusLabel,
  getAccountStatusColor,
} from "utils/accountStatusColors";
import AccountsInfoCards from "views/components/Accounts/AccountsInfoCards";
import FilesTab from "views/inventory/tabs/FilesTab";
import UserDefinedTab from "views/inventory/tabs/UserDefinedTab";
import SnapshotTab from "views/accounts/tabs/SnapshotTab";
import BalancesTab from "views/accounts/tabs/BalancesTab";
import EpsInstructionsTab from "views/accounts/tabs/EpsInstructionsTab";
import BorrowerTab from "views/accounts/tabs/BorrowerTab";
import TransactionsTab from "views/accounts/tabs/TransactionsTab";
import PaymentSchedTab from "views/accounts/tabs/PaymentSchedTab";
import LedgerCardTab from "views/accounts/tabs/LedgerCardTab";
import ReposTab from "views/accounts/tabs/ReposTab";
import AccountsInsuranceTab from "views/accounts/tabs/AccountsInsuranceTab";
import { useGetCalculusDetail } from "api/calculuses/service";
import InventoryDetail from "views/inventory/InventoryDetail";
import { FooterButton } from "components/Footer/components";
import AccountsRightDrawer from "views/accounts/components/AccountsRightDrawer";
import { BUTTON_STYLES, DRAWER_STYLES } from "components/Footer/constants";
import { FaMoneyBillTransfer } from "react-icons/fa6";
import { MdAttachMoney, MdBusinessCenter, MdMoreHoriz, MdPrint, MdSend } from "react-icons/md";
import SubHeader from "components/Navbars/SubHeader";
import { useClearListStateOnLeave, LIST_STATE_FLOWS } from "utils/listStateSessionStorage";

const ACCOUNTS_LIST_STATE_KEY = "dealertiq-accounts-list-state";

function mergeDefinedBalanceShape(base, overlay) {
  const out = {
    ...(base && typeof base === "object" && !Array.isArray(base) ? base : {}),
  };
  if (!overlay || typeof overlay !== "object" || Array.isArray(overlay)) return out;
  Object.keys(overlay).forEach((key) => {
    const v = overlay[key];
    if (v !== undefined) out[key] = v;
  });
  return out;
}

function mergeAccountDetailChunks(...chunks) {
  return chunks.filter(Boolean).reduce((acc, chunk) => {
    if (!acc) {
      const initial = { ...chunk };
      return initial;
    }
    const next = { ...acc, ...chunk };
    if (chunk.current_bal !== undefined) {
      next.current_bal = mergeDefinedBalanceShape(acc.current_bal, chunk.current_bal);
    }
    if (chunk.balances !== undefined) {
      next.balances = mergeDefinedBalanceShape(acc.balances, chunk.balances);
    }
    return next;
  }, null);
}

const AccountsDetail = ({ activeColor = "blue", isLightTheme,shellThemeVariant = "1"  }, ref) => {
  const { t } = useTranslation();
  const { id } = useParams();
  useClearListStateOnLeave(ACCOUNTS_LIST_STATE_KEY, LIST_STATE_FLOWS.accounts);
  const [activeTab, setActiveTab] = useState("SNAPSHOT");
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const deleteDealMutation = useDeleteDeal();
  const [tabLoading, setTabLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { data: account } = useGetAccount();

  const canViewTransactions =
    account?.permissions?.can_view_transactions === true ||
    account?.permissions?.can_veiw_transactions === true;

  const userDefinedTabRef = useRef();
  const epsInstructionsTabRef = useRef();
  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const [isSaving, setIsSaving] = useState(false);
  const dealInsuranceTabRef = useRef();
  const drawerRef = useRef();

  const [dealMainData, setDealMainData] = useState(null);
  const [borrowerDefaultTab, setBorrowerDefaultTab] = useState(null);
  const [collateralDefaultTab, setCollateralDefaultTab] = useState(null);

  // Local floating action menu state (Accounts Detail specific)
  const [actionsClosed, setActionsClosed] = useState(false);
  const [hoverStates, setHoverStates] = useState({ button: null });

  const handleHoverChange = useCallback((type, value) => {
    setHoverStates((prev) => ({ ...prev, [type]: value }));
  }, []);

  const getIconColor = useCallback(() => {
    return "#fff";
  }, []);

  const getButtonStyle = useCallback(
    (buttonId) => {
      const isHovered = hoverStates.button === buttonId;

      const baseStyle = {
        ...BUTTON_STYLES.base,
        backgroundColor: "#181b23",
        borderColor: "#fff",
        borderWidth: "1px",
        borderStyle: "solid",
        marginLeft: "4px",
        transform: "scale(1)",
        boxShadow: "none",
        filter: "none",
        zIndex: "auto",
      };

      const hoverStyle = isHovered
        ? {
          ...BUTTON_STYLES.hovered,
          backgroundColor: activeColorStyle.background,
          borderColor: activeColorStyle.borderColor,
          borderWidth: "3px",
          transform: "scale(1.25)",
          boxShadow:
            "0 8px 25px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 255, 255, 0.3)",
          filter: "brightness(1.2) contrast(1.1)",
          zIndex: 1000,
        }
        : {};

      return {
        ...baseStyle,
        ...hoverStyle,
      };
    },
    [hoverStates.button, activeColorStyle]
  );

  const openDrawer = useCallback((type, data) => {
    if (drawerRef.current && typeof drawerRef.current.open === "function") {
      drawerRef.current.open(type, data);
    }
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const isTheme2 = shellThemeVariant === "2";
  const tabScrollRef = useRef(null);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const calculusDetailEnabled =
    !(activeTab === "TRANSACTIONS" && !canViewTransactions);

  const { data, isLoading, isFetching } = useGetCalculusDetail(
    id,
    activeTab,
    undefined,
    calculusDetailEnabled
  );

  const shouldBootstrapBalances =
    !!id && calculusDetailEnabled && activeTab !== "BALANCES";

  const { data: balancesBootstrapData } = useGetCalculusDetail(
    id,
    "BALANCES",
    undefined,
    shouldBootstrapBalances
  );

  useEffect(() => {
    if (data && !initialDataLoaded) {
      setInitialDataLoaded(true);
    }
  }, [data, initialDataLoaded]);

  useEffect(() => {
    if (!data && !balancesBootstrapData) return;
    setDealMainData((prev) =>
      mergeAccountDetailChunks(prev, data, balancesBootstrapData)
    );
  }, [data, balancesBootstrapData]);

  const accountCardData = useMemo(
    () => mergeAccountDetailChunks(dealMainData, data, balancesBootstrapData),
    [dealMainData, data, balancesBootstrapData]
  );

  // Tab değiştiğinde sadece gerçekten yeni veri yüklenirken loading göster
  useEffect(() => {
    if (initialDataLoaded && isFetching && !isLoading) {
      setTabLoading(true);
      const timer = setTimeout(() => {
        setTabLoading(false);
      }, 200); // Daha kısa loading efekti
      return () => clearTimeout(timer);
    } else if (!isFetching) {
      setTabLoading(false);
    }
  }, [isFetching, isLoading, initialDataLoaded]);

  const tabs = [
    "SNAPSHOT",
    "BALANCES",
    "EPS INSTRUCTIONS",
    "BORROWER",
    "COLLATERAL",
    "TRANSACTIONS",
    "PAYMENT SCHED",
    // "LEDGER CARD",
    "INSURANCE",
    "REPOS",
    "FILES",
    "ECABINET",
    "USER DEFINED",
  ].filter((tab) => !(tab === "BORROWER" && dealMainData?.account_type === "wholesale"));

  const checkTabScroll = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) {
      setCanScrollTabsRight(false);
      return;
    }
    setCanScrollTabsRight(
      el.scrollLeft < el.scrollWidth - el.clientWidth - 1
    );
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setCanScrollTabsRight(false);
      return undefined;
    }
    const runCheck = () => {
      requestAnimationFrame(checkTabScroll);
    };
    runCheck();
    const scrollEl = tabScrollRef.current;
    scrollEl?.addEventListener("scroll", checkTabScroll);
    window.addEventListener("resize", runCheck);
    return () => {
      scrollEl?.removeEventListener("scroll", checkTabScroll);
      window.removeEventListener("resize", runCheck);
    };
  }, [checkTabScroll, tabs.length, id, isMobile]);

  const handleTabScrollRight = useCallback(() => {
    if (tabScrollRef.current && canScrollTabsRight) {
      tabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  }, [canScrollTabsRight]);

  // Tab display names with icons
  const getTabDisplayName = (tab) => {
    const tabTranslations = {
      "SNAPSHOT": t('accountsDetail.tabs.snapshot'),
      "BALANCES": t('accountsDetail.tabs.balances'),
      "EPS INSTRUCTIONS": t('accountsDetail.tabs.epsInstructions'),
      "BORROWER": t('accountsDetail.tabs.borrower'),
      "COLLATERAL": t('accountsDetail.tabs.collateral'),
      "TRANSACTIONS": t('accountsDetail.tabs.transactions'),
      "PAYMENT SCHED": t('accountsDetail.tabs.paymentSched'),
      // "LEDGER CARD": t('accountsDetail.tabs.ledgerCard'),
      "INSURANCE": t('accountsDetail.tabs.insurance'),
      "REPOS": t('accountsDetail.tabs.repos'),
      "FILES": t('accountsDetail.tabs.files'),
      "ECABINET": t('accountsDetail.tabs.ecabinet'),
      "USER DEFINED": t('accountsDetail.tabs.userDefined'),
    };
    return tabTranslations[tab] || tab;
  };

  // Deal yaşını hesapla - sadece bir kez hesapla
  const [dealAge, setDealAge] = useState(undefined);
  const [daysInDeal, setDaysInDeal] = useState("-");

  // İlk veri yüklendiğinde dealAge ve daysInDeal'i hesapla
  useEffect(() => {
    if (data && !dealAge && data.createdAt) {
      const dealDate = new Date(data.createdAt);
      const calculatedDealAge = Math.floor(
        (new Date() - dealDate) / (1000 * 60 * 60 * 24)
      );
      setDealAge(calculatedDealAge);
    }
  }, [data?.createdAt, dealAge]);

  useEffect(() => {
    if (data && daysInDeal === "-" && data.createdAt) {
      const calculatedDaysInDeal = Math.floor(
        (new Date() - new Date(data.createdAt)) / (1000 * 60 * 60 * 24)
      );
      setDaysInDeal(calculatedDaysInDeal);
    }
  }, [data?.createdAt, daysInDeal]);

  // Deal ID değiştiğinde değerleri sıfırla
  useEffect(() => {
    setDealAge(undefined);
    setDaysInDeal("-");
    setDealMainData(null);
  }, [id]);

  // Handle tab change
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
  }, []);

  // Delete handlers
  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDealMutation.mutateAsync({
        id: data?.deal_id,
      });
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting deal:", error);
      // Error handling is now done in the mutation hook
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  // Save fonksiyonu
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Aktif tab'a göre save işlemi yap (mevcut olanlar)
      if (
        activeTab === "INSURANCE" &&
        dealInsuranceTabRef.current &&
        typeof dealInsuranceTabRef.current.handleSave === "function"
      ) {
        await dealInsuranceTabRef.current.handleSave();
      }
      if (activeTab === "USER DEFINED" && userDefinedTabRef.current) {
        await userDefinedTabRef.current.handleSave();
      }
      if (
        activeTab === "EPS INSTRUCTIONS" &&
        epsInstructionsTabRef.current &&
        typeof epsInstructionsTabRef.current.handleSave === "function"
      ) {
        await epsInstructionsTabRef.current.handleSave();
      }
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  // İlk yükleme sırasında loading göster
  if (isLoading && !initialDataLoaded) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ color: activeColorStyle.borderColor }}
        >
          <span className="sr-only">{t('common.loading')}</span>
        </div>
        <span className="ml-2" style={{ color: activeColorStyle.borderColor }}>
          {t('accountsDetail.loadingData')}
        </span>
      </div>
    );
  }

  return (
    <div className="content" style={{ minHeight: "80vh", paddingTop: isMobile ? "7rem" : null }}>
      <div
        style={{ width: "100%" }}
      >
        <SubHeader
          activeColor={activeColor}
          back={-1}
          backFallback="/accounts"
          title={"Account " + (data?.booking_id || id)}
          status={formatAccountStatusLabel(data?.act_status, {
            isLock: Boolean(data?.is_lock),
            flags: data?.flags,
            isBankruptcy: Boolean(data?.is_bankruptcy),
          })}
          statusColor={getAccountStatusColor(data?.act_status, {
            flags: data?.flags,
            isBankruptcy: Boolean(data?.is_bankruptcy),
          })}
          theme2ScrollGapFill={shellThemeVariant === "2"}
          isLightTheme={isLightTheme}
        />
        {/* Accounts Info Cards */}
        <div
          className={classNames("account-detail-info-cards", {
            "account-detail-info-cards--theme2": isTheme2,
          })}
        >
          <AccountsInfoCards
            data={accountCardData}
            isLightTheme={isLightTheme}
            isTheme2={isTheme2}
            getActiveColorStyle={() => activeColorStyle}
          />
        </div>

        {/* Tab Menüsü ve İçerik - Ayrı Bölüm */}
        <div
          className={
            isMobile
              ? "w-100 account-detail-tabs detail-mobile-tabs"
              : "w-100 account-detail-tabs"
          }
        >
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
            {isMobile ? (
              <div className="d-flex align-items-center flex-grow-1 min-width-0 w-100">
            <div
              ref={tabScrollRef}
              className="flex-grow-1 channel-tabs-scroll"
              style={{ minWidth: 0 }}
            >
              <Nav
                tabs
                className="border-0"
                style={{
                  borderBottom: "none",
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  minWidth: "max-content",
                  width: "auto",
                  paddingLeft: 0,
                  paddingRight: 0,
                }}
              >
                {tabs.map((tab) => (
                  <NavItem key={tab} style={{ flex: "0 0 auto" }}>
                    <NavLink
                      href="#"
                      className={
                        activeTab === tab
                          ? "active font-weight-bold"
                          : "text-muted"
                      }
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 14,
                        position: "relative",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        color:
                          activeTab === tab
                            ? activeColorStyle.borderColor
                            : undefined,
                        fontWeight: activeTab === tab ? 700 : 500,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabChange(tab);
                      }}
                    >
                      {getTabDisplayName(tab).toUpperCase()}
                      {activeTab === tab && (
                        <div
                          style={{
                            height: 3,
                            background: activeColorStyle.borderColor,
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: -8,
                          }}
                        />
                      )}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </div>
            {canScrollTabsRight && (
              <button
                type="button"
                className="btn btn-link p-0 flex-shrink-0"
                onClick={handleTabScrollRight}
                style={{
                  color: activeColorStyle.borderColor,
                  lineHeight: 1,
                }}
                aria-label={t("common.scrollTabs")}
              >
                <i
                  className="tim-icons icon-minimal-right"
                  style={{ fontSize: 20 }}
                />
              </button>
            )}
              </div>
            ) : (
              <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
                {tabs.map((tab) => (
                  <NavItem key={tab}>
                    <NavLink
                      href="#"
                      className={
                        activeTab === tab
                          ? "active font-weight-bold"
                          : "text-muted"
                      }
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 14,
                        position: "relative",
                        cursor: "pointer",
                        color:
                          activeTab === tab
                            ? activeColorStyle.borderColor
                            : undefined,
                        fontWeight: activeTab === tab ? 700 : 500,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabChange(tab);
                      }}
                    >
                      {getTabDisplayName(tab).toUpperCase()}
                      {activeTab === tab && (
                        <div
                          style={{
                            height: 3,
                            background: activeColorStyle.borderColor,
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: -8,
                          }}
                        />
                      )}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            )}

            {/* Desktop View Save Button */}
            {account?.permissions?.deals_update &&
              (activeTab === "USER DEFINED" ||
                activeTab === "EPS INSTRUCTIONS") && (
                <div className="d-none d-lg-flex align-items-center">
                  <button
                    className="btn btn-sm"
                    style={{
                      ...activeColorStyle,
                      color: "white",
                      cursor: "pointer",
                      margin: 0
                    }}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t('common.saving')}
                      </>
                    ) : (
                      t('common.save')
                    )}
                  </button>
                </div>
              )}

            {/* Mobile View Save Button */}
            {account?.permissions?.deals_update &&
              (activeTab === "SNAPSHOT" ||
                activeTab === "USER DEFINED" ||
                activeTab === "EPS INSTRUCTIONS") && (
                <div className="d-flex d-lg-none flex-column w-100 mt-2">
                  <button
                    className="btn btn-sm w-100"
                    style={{
                      ...activeColorStyle,
                      color: "white",
                      cursor: "pointer",
                      padding: '12px',
                      margin: 0
                    }}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t('common.saving')}
                      </>
                    ) : (
                      t('common.save')
                    )}
                  </button>
                </div>
              )}
          </div>
          <TabContent activeTab={activeTab}>
            <TabPane tabId={activeTab}>
              {tabLoading && isFetching ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "100px" }}
                >
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                    style={{ color: activeColorStyle.borderColor }}
                  >
                    <span className="sr-only">{t('common.loading')}</span>
                  </div>
                  <span
                    className="ml-2"
                    style={{
                      color: activeColorStyle.borderColor,
                      fontSize: "14px",
                    }}
                  >
                    {t('common.updating')}
                  </span>
                </div>
              ) : (
                <>
                  {activeTab === "SNAPSHOT" && (
                    <SnapshotTab
                      data={data}
                      activeColor={activeColor}
                      openDrawer={openDrawer}
                      accountData={data}
                      permission={account?.permissions}
                      isTheme2={isTheme2}
                      id={id}
                    />
                  )}
                  {activeTab === "BORROWER" && (
                    <BorrowerTab
                      activeColor={activeColor}
                      account={account}
                      id={data?.customer?.customer_id}
                      defaultTab={borrowerDefaultTab}
                      shellThemeVariant={shellThemeVariant}
                    />
                  )}
                  {activeTab === "INSURANCE" && (
                    <AccountsInsuranceTab
                      data={data?.insurance_history}
                      activeColor={activeColor}
                    />
                  )}
                  {activeTab === "FILES" && (
                    <FilesTab
                      module="accounts"
                      permission={{
                        add_files: account?.permissions?.accounts_files_add,
                        delete_files: account?.permissions?.accounts_files_add,
                      }}
                      id={data?.id}
                      data={data?.files}
                      type="file"
                      isLoading={isLoading}
                      activeColor={activeColor}
                    />
                  )}
                  {activeTab === "ECABINET" && (
                    <FilesTab
                      module="accounts"
                      type="ecabinet"
                      permission={{
                        add_files: account?.permissions?.accounts_ecabinet_add,
                        delete_files:
                          account?.permissions?.accounts_ecabinet_add,
                      }}
                      id={data?.id}
                      data={data?.ecabinets}
                      isLoading={isLoading}
                      activeColor={activeColor}
                    />
                  )}
                  {activeTab === "USER DEFINED" && (
                    <UserDefinedTab
                      permission={account?.permissions}
                      data={data?.user_defineds}
                      activeColor={activeColor}
                      id={id}
                      module="accounts"
                      ref={userDefinedTabRef}
                    />
                  )}
                  {activeTab === "LEDGER CARD" && <LedgerCardTab data={data} />}
                  {activeTab === "BALANCES" && (
                    <BalancesTab data={dealMainData ?? data} />
                  )}
                  {activeTab === "EPS INSTRUCTIONS" && (
                    <EpsInstructionsTab
                      ref={epsInstructionsTabRef}
                      id={id}
                      data={data}
                    />
                  )}
                  {activeTab === "COLLATERAL" && (
                    <div className="card">
                      <div className="card-header">
                        <h4 className="mb-0" style={{ fontSize: "1rem" }}>
                          <a
                            href={`/inventory/${data?.inventory?.documentId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "inherit",
                            }}
                          >
                            {data?.inventory?.vin}
                          </a>
                        </h4>
                      </div>
                      <div className="card-body mt-0 pt-0">
                        <InventoryDetail
                          inventoryId={data?.inventory?.documentId}
                          isEmbedded={true}
                          activeColor={activeColor}
                          defaultTab={collateralDefaultTab}
                          shellThemeVariant={shellThemeVariant}
                        />
                      </div>
                    </div>
                  )}
                  {activeTab === "TRANSACTIONS" && (
                    <TransactionsTab
                      data={canViewTransactions ? data?.payments : undefined}
                      accountData={data}
                      activeColor={activeColor}
                      openDrawer={openDrawer}
                      canViewTransactions={canViewTransactions}
                    />
                  )}
                  {activeTab === "PAYMENT SCHED" && (
                    <PaymentSchedTab data={data?.payment_schedule} activeColor={activeColor} />
                  )}
                  {activeTab === "REPOS" && <ReposTab data={data?.repos} isLightTheme={isLightTheme} />}
                </>
              )}
            </TabPane>
          </TabContent>
        </div>

        {/* Remove Button - Bottom of Screen */}
        {account?.permissions?.deals_delete &&
          (activeTab === "GENERAL" || activeTab === "CUSTOMER") && (
            <div className="d-flex justify-content-center mt-4 mb-4">
              <button
                className="btn btn-sm"
                style={{
                  background:
                    "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
                  backgroundImage:
                    "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
                  borderColor: "#fd5d93",
                  color: "white",
                }}
                onClick={handleDeleteClick}
                disabled={deleteDealMutation.isPending}
              >
                {deleteDealMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t('common.removing')}
                  </>
                ) : (
                  t('common.remove')
                )}
              </button>
            </div>
          )}
      </div>

      {/* Floating Action Buttons - Accounts Detail */}
      <div style={DRAWER_STYLES.floatingContainer}>
        {!actionsClosed && (
          <>
            <FooterButton
              id="contact"
              icon={<MdBusinessCenter size={25} />}
              onClick={() => openDrawer("contact")}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "contact" : null)
              }
              style={getButtonStyle("contact")}
              iconColor={getIconColor()}
            />

            <FooterButton
              id="promise"
              icon={<FaMoneyBillTransfer size={25} />}
              onClick={() => openDrawer("promise")}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "promise" : null)
              }
              style={getButtonStyle("promise")}
              iconColor={getIconColor()}
            />

            <FooterButton
              id="payment"
              icon={<MdAttachMoney size={25} />}
              onClick={() => openDrawer("payment")}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "payment" : null)
              }
              style={getButtonStyle("payment")}
              iconColor={getIconColor()}
            />

            {/* <FooterButton
              id="notes"
              icon={<MdStickyNote2 size={25} />}
              onClick={() => openDrawer("notes")}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "notes" : null)
              }
              style={getButtonStyle("notes")}
              iconColor={getIconColor()}
            />

 */}

            <FooterButton
              id="print-drawer"
              icon={<MdPrint size={25} />}
              onClick={() => openDrawer("print")}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "print" : null)
              }
              style={getButtonStyle("print")}
              iconColor={getIconColor()}
            />
            <FooterButton
              id="send"
              icon={<MdSend size={25} />}
              onClick={() => openDrawer("send")}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "send" : null)
              }
              style={getButtonStyle("send")}
              iconColor={getIconColor()}
            />
            <FooterButton
              id="more"
              icon={<MdMoreHoriz size={25} />}
              onClick={() => openDrawer("more")}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "more" : null)
              }
              style={getButtonStyle("more")}
              iconColor={getIconColor()}
            />
          </>
        )}
        <FooterButton
          id="actions-toggle"
          icon={
            <i
              className={`tim-icons  ${actionsClosed ? "icon-minimal-up" : "icon-minimal-down"
                }`}
            />
          }
          onHoverChange={(isHovered) =>
            handleHoverChange("button", isHovered ? "actions-toggle" : null)
          }
          onClick={() => setActionsClosed(!actionsClosed)}
          style={getButtonStyle("actions-toggle")}
          iconColor={getIconColor()}
        />
      </div>

      {/* Right Drawer */}
      <AccountsRightDrawer
        ref={drawerRef}
        shellThemeVariant={shellThemeVariant}
        accountData={data}
        activeColor={activeColor}
        status={data?.act_status}
        promiseSummary={data?.promise_payment}
        activeColorStyle={activeColorStyle}
        isLightTheme={isLightTheme}
        bookingId={id}
        worksheetId={data?.worksheet?.worksheet_id}
        insurance_history={data?.insurance_history}
        flags={data?.flags}
        permission={account?.permissions}
        onAddBorrowerFile={() => {
          handleTabChange("BORROWER");
          setBorrowerDefaultTab("FILES");
        }}
        onAddCollateralImage={() => {
          handleTabChange("COLLATERAL");
          setCollateralDefaultTab("IMAGES");
        }}
        onAddCollateralFile={() => {
          handleTabChange("COLLATERAL");
          setCollateralDefaultTab("FILES");
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">Delete Deal</h6>
        </div>
        <ModalBody className="text-center">
          <p>Are you sure you want to delete this deal?</p>
          <p className="text-muted">This action cannot be undone.</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteDealMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            style={{
              background:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteDealMutation.isPending}
          >
            {deleteDealMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Deleting...
              </>
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default AccountsDetail;
